// ─────────────────────────────────────────────────────────────────────────────
// V2Suppliers — /dashboard/managesuppliers in the v2 design.
// Endpoints are v1's: GET/POST /api/suppliers, PUT/DELETE /api/suppliers/:id,
// GET /api/purchases + /api/purchase-orders (filtered client-side, as v1 does).
// Outstanding = purchased − paid + opening balance (v1 formula).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Money, Sheet, Skel, inr, useMedia } from "./primitives";
import { Field, PageHead } from "./kit";

const BASE = `${API_BASE}/api`;
const json = () => ({ "Content-Type": "application/json", "auth-token": localStorage.getItem("token") });
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const sid = (x) => x?.supplierId?._id || x?.supplierId;

export default function V2Suppliers({ showAlert }) {
  usePageTitle("Suppliers");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("suppliers", "write");
  const isNarrow = useMedia("(max-width: 900px)");
  const [list, setList] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [removing, setRemoving] = useState(null);
  const addBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, o] = await Promise.all([
        fetch(`${BASE}/suppliers`, { headers: json() }).then((r) => r.json()),
        fetch(`${BASE}/purchases`, { headers: json() }).then((r) => r.json()).catch(() => []),
        fetch(`${BASE}/purchase-orders`, { headers: json() }).then((r) => r.json()).catch(() => []),
      ]);
      setList(Array.isArray(s) ? s : []); setPurchases(Array.isArray(p) ? p : []); setPos(Array.isArray(o) ? o : []);
    } catch { setList([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const m = {};
    list.forEach((s) => {
      const ps = purchases.filter((p) => sid(p) === s._id);
      const os = pos.filter((p) => sid(p) === s._id);
      const bought = ps.reduce((a, p) => a + (p.grandTotal || 0), 0);
      const paid = ps.reduce((a, p) => a + (p.amountPaid || 0), 0);
      m[s._id] = { ps, os, bought, paid, due: bought - paid + (s.openingBalance || 0), activePOs: os.filter((p) => ["DRAFT", "SENT", "PARTIAL"].includes(p.status)) };
    });
    return m;
  }, [list, purchases, pos]);

  const filtered = list.filter((s) => {
    const t = q.trim().toLowerCase();
    return !t || `${s.supplierName} ${s.supplierGSTIN || ""} ${s.contactPerson || ""} ${s.phone || ""} ${s.email || ""}`.toLowerCase().includes(t);
  });
  useEffect(() => { if (!isNarrow && !selId && filtered.length) setSelId(filtered[0]._id); }, [filtered, isNarrow, selId]);
  const sel = list.find((s) => s._id === selId) || null;
  const st = sel ? stats[sel._id] : null;
  const totalDue = Object.values(stats).reduce((a, s) => a + Math.max(s.due, 0), 0);

  const detail = sel && st && (
    <div className="v2c-detail" key={sel._id}>
      <div className="v2c-hero">
        <span className="v2-avatar v2-avatar--client" style={{ width: 72, height: 72, fontSize: 26 }}>{ini(sel.supplierName)}</span>
        <div className="v2c-hero-text">
          <h2 className="v2c-name">{sel.supplierName}</h2>
          {sel.supplierGSTIN ? <span className="v2c-gstin"><span className="v2-mono">{sel.supplierGSTIN}</span></span> : <span className="v2-row-sub">No GSTIN on file</span>}
        </div>
        {canWrite && <div className="v2c-hero-actions"><button type="button" className="v2-btn v2-btn--sm" onClick={() => setForm({ edit: sel })}><Glyph name="note" size={15} /> Edit</button></div>}
      </div>
      <div className="v2l-stats">
        <div className="v2l-stat"><span>Purchased</span><strong>{inr(st.bought, { decimals: 2 })}</strong></div>
        <div className="v2l-stat"><span>Paid</span><strong className="is-good">{inr(st.paid, { decimals: 2 })}</strong></div>
        <div className={`v2l-stat v2l-stat--hero${st.due > 0 ? "" : " is-clear"}`}><span>{st.due > 0 ? "You owe" : "Settled"}</span><strong><Money value={Math.abs(st.due)} /></strong></div>
      </div>
      {sel.openingBalance ? <p className="v2-row-sub">Includes opening balance of {inr(sel.openingBalance, { decimals: 2 })}</p> : null}
      <div className="v2c-quick">
        <button type="button" className="v2c-quick-btn" onClick={() => navigate("/dashboard/purchase-orders")}><Duo name="truck" size={26} /><span>New PO</span></button>
        <button type="button" className="v2c-quick-btn" onClick={() => navigate("/dashboard/purchase-entry")}><Duo name="invoice" size={26} /><span>Bill</span></button>
        <a className={`v2c-quick-btn${sel.email ? "" : " is-off"}`} href={sel.email ? `mailto:${sel.email}` : undefined}><Duo name="mail" size={26} /><span>Email</span></a>
        <a className={`v2c-quick-btn${sel.phone ? "" : " is-off"}`} href={sel.phone ? `tel:${sel.phone}` : undefined}><Duo name="phone" size={26} /><span>Call</span></a>
      </div>
      <div className="v2c-section-title">Contact</div>
      <div className="v2-inset">
        {[["clients", "Contact person", sel.contactPerson], ["phone", "Phone", sel.phone, sel.phone && `tel:${sel.phone}`], ["mail", "Email", sel.email, sel.email && `mailto:${sel.email}`], ["pin", "Address", sel.address]].map(([g, l, v, href]) => {
          const body = <><Duo name={g} size={24} /><span className="v2-row-main"><span className="v2-row-sub">{l}</span><span className={`v2-row-title v2-wrap${v ? "" : " v2c-missing"}`}>{v || "Not added"}</span></span>{href && <Glyph name="arrowUpRight" size={15} className="v2-inset-chev" />}</>;
          return href ? <a key={l} className="v2-inset-row v2c-info" href={href}>{body}</a> : <div key={l} className="v2-inset-row v2-inset-row--static v2c-info">{body}</div>;
        })}
      </div>
      {st.activePOs.length > 0 && (<>
        <div className="v2c-section-title">Open purchase orders · {st.activePOs.length}</div>
        <div className="v2-inset">{st.activePOs.slice(0, 5).map((p) => (
          <button key={p._id} type="button" className="v2-inset-row" onClick={() => navigate("/dashboard/purchase-orders")}>
            <span className="v2-row-main"><span className="v2-row-title">{p.poNumber}</span><span className="v2-row-sub">{fmtDate(p.poDate)} · {p.status.toLowerCase()}</span></span>
            <b>{inr(p.grandTotal, { decimals: 2 })}</b><Glyph name="chevronRight" size={15} className="v2-inset-chev" />
          </button>))}</div>
      </>)}
      <div className="v2c-section-title">Recent bills{st.ps.length ? ` · ${st.ps.length}` : ""}</div>
      {st.ps.length ? (
        <div className="v2-inset">{[...st.ps].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate)).slice(0, 6).map((p) => (
          <button key={p._id} type="button" className="v2-inset-row" onClick={() => navigate("/dashboard/purchase-entry")}>
            <span className="v2-row-main"><span className="v2-row-title">{p.purchaseNumber}{p.supplierInvoiceNo ? ` · ${p.supplierInvoiceNo}` : ""}</span><span className="v2-row-sub">{fmtDate(p.purchaseDate)}</span></span>
            <span className="v2-row-end"><b>{inr(p.grandTotal, { decimals: 2 })}</b>
              <span className={`v2-status v2-status--${p.status === "PAID" ? "good" : p.status === "PARTIAL" ? "warn" : "bad"}`}>{p.status === "PAID" ? "Paid" : p.status === "PARTIAL" ? `${inr(p.balanceDue)} due` : "Unpaid"}</span></span>
          </button>))}</div>
      ) : <p className="v2-row-sub">No purchase bills yet.</p>}
      {sel.notes && <><div className="v2c-section-title">Notes</div><p className="v2-row-sub">{sel.notes}</p></>}
      {canWrite && <button type="button" className="v2c-danger-link" onClick={() => setRemoving(sel)}><Glyph name="close" size={14} stroke={2} /> Delete supplier</button>}
    </div>
  );

  const detailOnly = isNarrow && sel;
  return (
    <div className="v2p">
      {!detailOnly && <PageHead eyebrow="Payables" title="Suppliers" sub={loading ? "Loading…" : `${list.length} supplier${list.length === 1 ? "" : "s"} · ${inr(totalDue)} payable`}
        actions={canWrite && <button ref={addBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setForm({})}><Glyph name="plus" size={17} stroke={2.2} /> New supplier</button>} />}
      {detailOnly && <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> All suppliers</button>}
      <div className={`v2c-split${detailOnly ? " is-detail" : ""}`}>
        {!detailOnly && (
          <section className="v2-card v2c-listcard">
            <div className="v2-field--search v2c-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, GSTIN, contact" aria-label="Search suppliers" /></div>
            {loading ? <div className="v2-list" style={{ marginTop: 10 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={54} r={16} />)}</div>
              : filtered.length === 0 ? <div className="v2c-empty"><Duo name="building" size={44} /><strong>{q ? "No matches" : "No suppliers yet"}</strong>{!q && canWrite && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setForm({})}>New supplier</button>}</div>
              : <div className="v2c-list">{filtered.map((s) => {
                const due = stats[s._id]?.due || 0;
                return (
                  <button key={s._id} type="button" className={`v2l-row${s._id === selId && !isNarrow ? " is-sel" : ""}`} onClick={() => setSelId(s._id)}>
                    <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13.5 }}>{ini(s.supplierName)}</span>
                    <span className="v2-row-main"><span className="v2-row-title">{s.supplierName}</span><span className="v2-row-sub">{s.contactPerson || s.supplierGSTIN || s.phone || "—"}</span></span>
                    {due > 0 && <span className="v2l-row-bal is-due">{inr(due)}</span>}
                  </button>);
              })}</div>}
          </section>
        )}
        {(!isNarrow || sel) && <section className={isNarrow ? "" : "v2-card v2c-detailcard"}>{detail || <div className="v2c-empty"><Duo name="building" size={44} /><span>Choose a supplier.</span></div>}</section>}
      </div>
      <SupplierForm state={form} originRef={addBtn} onClose={() => setForm(null)}
        onSaved={(d, msg) => { setForm(null); showAlert?.(msg, "success"); load(); if (d?._id) setSelId(d._id); }} />
      <Sheet open={!!removing} onClose={() => setRemoving(null)} width={440} title={`Delete ${removing?.supplierName || ""}?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setRemoving(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try {
              const r = await fetch(`${BASE}/suppliers/${removing._id}`, { method: "DELETE", headers: json() });
              const d = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(d.error || "Couldn't delete");
              showAlert?.("Supplier deleted", "success"); setRemoving(null); setSelId(null); load();
            } catch (e) { setRemoving((x) => ({ ...x, error: e.message })); }
          }}>Delete supplier</button></>}>
        <p className="v2c-remove-text">The supplier is removed from your directory. Purchase bills and orders already recorded stay as they are.</p>
        {removing?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {removing.error}</div>}
      </Sheet>
    </div>
  );
}

const EMPTY = { supplierName: "", supplierGSTIN: "", contactPerson: "", phone: "", email: "", address: "", openingBalance: "", notes: "" };
function SupplierForm({ state, originRef, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keep = useRef(state); if (state) keep.current = state;
  const s = keep.current;
  const edit = s?.edit;
  useEffect(() => {
    if (!state) return;
    setErr("");
    setF(state.edit ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, state.edit[k] ?? ""])) : EMPTY);
  }, [state]);
  if (!s) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: k === "supplierGSTIN" ? e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 15) : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (!f.supplierName.trim()) { setErr("Supplier name is required."); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(edit ? `${BASE}/suppliers/${edit._id}` : `${BASE}/suppliers`, {
        method: edit ? "PUT" : "POST", headers: json(), body: JSON.stringify({ ...f, openingBalance: parseFloat(f.openingBalance) || 0 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save");
      onSaved(d, edit ? "Supplier updated" : "Supplier added");
    } catch (x) { setErr(x.message); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={!!state} onClose={onClose} originRef={edit ? undefined : originRef} width={560} title={edit ? "Edit supplier" : "New supplier"}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2s-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Add supplier"}</button></>}>
      <form id="v2s-form" className="v2-form" onSubmit={save}>
        <Field label="Business name"><input className="v2-input" autoFocus value={f.supplierName} onChange={set("supplierName")} placeholder="Supplier Pvt. Ltd." /></Field>
        <div className="v2-form-row">
          <Field label="GSTIN" optional hint="Decides CGST+SGST vs IGST on purchases"><input className="v2-input v2-mono" value={f.supplierGSTIN} onChange={set("supplierGSTIN")} /></Field>
          <Field label="Contact person" optional><input className="v2-input" value={f.contactPerson} onChange={set("contactPerson")} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Phone" optional><input className="v2-input" type="tel" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Email" optional><input className="v2-input" type="email" value={f.email} onChange={set("email")} /></Field>
        </div>
        <Field label="Address" optional><input className="v2-input" value={f.address} onChange={set("address")} /></Field>
        <div className="v2-form-row">
          <Field label="Opening balance" optional hint="What you already owed them before EazyBilling"><input className="v2-input" type="number" step="0.01" value={f.openingBalance} onChange={set("openingBalance")} /></Field>
          <Field label="Notes" optional><input className="v2-input" value={f.notes} onChange={set("notes")} /></Field>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
